import type { FrameApplyContext } from "./companion-frame-reducer";

const EMPTY_TERMINAL_CURSOR_HOLD_MS = 3_000;
const MIN_FINAL_CURSOR_HOLD_MS = 15_000;
const MAX_FINAL_CURSOR_HOLD_MS = 45_000;

export function terminalCursorHoldMs(content?: string): number {
  const text = content?.trim();
  if (!text) return EMPTY_TERMINAL_CURSOR_HOLD_MS;
  const estimatedReadingMs = Math.ceil(text.length / 14) * 1_000 + 3_000;
  return Math.min(
    MAX_FINAL_CURSOR_HOLD_MS,
    Math.max(MIN_FINAL_CURSOR_HOLD_MS, estimatedReadingMs),
  );
}

export function scheduleTerminalCursorRemoval(
  ctx: FrameApplyContext,
  cursorKey: string,
  executionId: string,
  delayMs: number,
): void {
  if (ctx.canvasStore.getState().agentCursors.get(cursorKey)?.question) return;
  clearTimeout(ctx.cursorRemovalTimers.get(cursorKey));
  const timer = ctx.defer(() => {
    if (ctx.cursorRemovalTimers.get(cursorKey) !== timer) return;
    const cursor = ctx.canvasStore.getState().agentCursors.get(cursorKey);
    if (cursor?.executionId && cursor.executionId !== executionId) {
      ctx.cursorRemovalTimers.delete(cursorKey);
      return;
    }
    const exec = ctx.companionStore.getState().executions.get(executionId);
    if (
      exec &&
      (exec.status === "running" || exec.status === "waiting_approval")
    ) {
      ctx.cursorRemovalTimers.delete(cursorKey);
      return;
    }
    ctx.canvasStore.getState().removeAgentCursor(cursorKey);
    ctx.cursorRemovalTimers.delete(cursorKey);
  }, delayMs);
  ctx.cursorRemovalTimers.set(cursorKey, timer);
}

/** Late stream delivery must not cancel the completed cursor's only expiry. */
export function retainTerminalCursor(
  ctx: FrameApplyContext,
  cursorKey: string,
  executionId?: string,
): void {
  const cursor = ctx.canvasStore.getState().agentCursors.get(cursorKey);
  const resolvedExecutionId = executionId ?? cursor?.executionId;
  if (!cursor || cursor.question || !resolvedExecutionId) return;
  const status = ctx.companionStore
    .getState()
    .executions.get(resolvedExecutionId)?.status;
  if (
    status !== "completed" &&
    status !== "failed" &&
    status !== "cancelled" &&
    status !== "paused"
  )
    return;
  ctx.canvasStore.getState().setAgentCursor({ ...cursor, frozen: true });
  scheduleTerminalCursorRemoval(
    ctx,
    cursorKey,
    resolvedExecutionId,
    terminalCursorHoldMs(cursor.contentFull ?? cursor.content),
  );
}
